import React, { useState, useEffect } from 'react';
import { Target, Eye, Building2, Users, Award, Shield, Zap, Globe } from 'lucide-react';

const MissionVisionPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const missionPoints = [
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Technical Excellence",
      description: "We deliver technically excellent and innovative solutions that add value for all stakeholders and operate globally as professional consulting engineers."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Defect-Free Designs",
      description: "We strive to deliver defect-free engineering designs, ensuring clients achieve the best measurable value through cost avoidance, design optimization, and construction savings."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Customer Satisfaction",
      description: "We aim to be a single-source engineering provider, offering unparalleled knowledge and technological expertise at competitive costs while remaining responsive to customers' needs."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Customer-Oriented Solutions",
      description: "We deliver safe, innovative, reliable, and dynamic solutions driven by the excellence of our people."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "High-Quality Consultancy",
      description: "We provide high-quality, time-bound, cost-effective, and technically superior consultancy services through a highly qualified and committed workforce."
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Trusted Relationships",
      description: "We build trusted relationships with clients based on integrity, honesty, hard work, and accountability in a collaborative, solution-oriented environment."
    }
  ];

  const visionPoints = [
    {
      title: "Leading Professional Services Provider",
      description: "To be a leading provider of high-quality engineering and project management services, adding significant value to our customers' businesses."
    },
    {
      title: "Core Values and Commitment",
      description: "Our vision embodies our identity and reflects our core values. We aim to provide unmatched performance, unparalleled accountability, and the highest level of satisfaction in the industry."
    },
    {
      title: "Internationally Respected Consultant",
      description: "To be an internationally respected engineering consultancy offering comprehensive solutions."
    },
    {
      title: "Client-Centric Excellence",
      description: "We are committed to meeting our clients' needs for safe, reliable, and operationally efficient designs through cost-effective engineering and construction management services."
    }
  ];

  const additionalMissionPoints = [
    {
      title: "Efficient Solutions",
      description: "Our focus is to provide the most efficient and cost-effective solutions, prioritizing the right approach over the easiest one."
    },
    {
      title: "Dependable Services",
      description: "We offer the highest quality, most dependable, and cost-effective engineering, design, and project management services in the industry."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative h-screen bg-gray-900 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2076&q=80" 
            alt="Modern Engineering Construction"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-800/80 to-[#B91C1C]/40"></div>
        </div>

        <div className="relative z-10 flex items-center justify-center h-full px-4">
          <div className={`text-center text-white max-w-6xl mx-auto transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            
           

            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Our <span className="text-red-400">Mission</span> & <span className="text-red-400">Vision</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed mb-8">
              Engineering Excellence • Building Tomorrow • Delivering Trust
            </p>
            
            <div className="text-lg text-gray-300 italic">
              "COMING TOGETHER IS A BEGINNING, STAYING TOGETHER IS PROGRESS, WORKING TOGETHER IS SUCCESS"
            </div>
          </div>
        </div>

      </div>

      {/* Mission Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#B91C1C]/10 rounded-full mb-6">
              <Target className="w-8 h-8 text-[#B91C1C]" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-[#B91C1C]">Mission</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Committed to delivering excellence in engineering solutions while building lasting partnerships
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {missionPoints.map((point, index) => (
              <div
                key={index}
                className={`bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center justify-center w-16 h-16 bg-[#B91C1C]/10 rounded-xl mb-6 mx-auto">
                  <div className="text-[#B91C1C]">{point.icon}</div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                  {point.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-center">
                  {point.description}
                </p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {additionalMissionPoints.map((point, index) => (
              <div key={index} className="bg-[#B91C1C]/5 rounded-2xl p-8 border border-[#B91C1C]/10">
                <h3 className="text-2xl font-bold text-[#B91C1C] mb-4">{point.title}</h3>
                <p className="text-gray-700 leading-relaxed">
                  {point.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
 <div className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h3 className="text-3xl font-bold mb-8">Our Core Values</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="space-y-2">
              <Building2 className="w-12 h-12 text-[#B91C1C] mx-auto" />
              <h4 className="font-semibold">Excellence</h4>
            </div>
            <div className="space-y-2">
              <Users className="w-12 h-12 text-blue-400 mx-auto" />
              <h4 className="font-semibold">Integrity</h4>
            </div>
            <div className="space-y-2">
              <Zap className="w-12 h-12 text-green-400 mx-auto" />
              <h4 className="font-semibold">Innovation</h4>
            </div>
            <div className="space-y-2">
              <Award className="w-12 h-12 text-yellow-400 mx-auto" />
              <h4 className="font-semibold">Quality</h4>
            </div>
          </div>
        </div>
      </div>
      {/* Vision Section */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
              <Eye className="w-8 h-8 text-blue-700" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-blue-700">Vision</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Aspiring to be the global leader in engineering consultancy and infrastructure development
            </p>
          </div>

          <div className="space-y-8 mb-16">
            {visionPoints.map((point, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-8 shadow-lg border border-gray-100 ${
                  isVisible ? 'translate-x-0 opacity-100' : index % 2 === 0 ? '-translate-x-10 opacity-0' : 'translate-x-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-xl">
                      <span className="text-2xl font-bold text-blue-700">{index + 1}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {point.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-lg">
                      {point.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="relative rounded-3xl overflow-hidden">
            <img 
              src="https://plus.unsplash.com/premium_photo-1694475444824-d470e1cb7a3c?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Future Infrastructure Development"
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-[#B91C1C]/80 flex items-center justify-center">
              <div className="text-center text-white px-8">
                <h3 className="text-3xl md:text-4xl font-bold mb-4">
                  Building Tomorrow's Infrastructure Today
                </h3>
                <p className="text-xl text-gray-200">
                  Committed to excellence, innovation, and sustainable development
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Core Values */}
     
    </div>
  );
};

export default MissionVisionPage;